import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import { BASE_URL } from "../config";

type SharedPhoto = {
  imageUrl: string;
  description: string | null;
};

type SharedProject = {
  name: string;
  description: string;
  photos: SharedPhoto[];
};

function imageSource(imageUrl: string) {
  try {
    return new URL(imageUrl, BASE_URL).toString();
  } catch {
    return imageUrl;
  }
}

export default function ViewProject() {
  const { token = "" } = useParams();
  const [project, setProject] = useState<SharedProject | null>(null);
  const [status, setStatus] = useState<"loading" | "not-found" | "error">(
    "loading",
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setStatus("loading");
      setProject(null);
      try {
        // This is a same-origin public API route, separate from the React URL.
        const response = await fetch(
          `${BASE_URL}project/${encodeURIComponent(token)}`,
          {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );
        if (response.status === 404) {
          setStatus("not-found");
          return;
        }
        if (!response.ok) throw new Error("Unable to load project");
        const data = (await response.json()) as SharedProject;
        if (
          !data ||
          typeof data.name !== "string" ||
          !Array.isArray(data.photos)
        ) {
          throw new Error("Unexpected project response");
        }
        setProject(data);
        setStatus("loading");
      } catch (error) {
        if ((error as Error).name !== "AbortError") setStatus("error");
      }
    };
    load();
    return () => controller.abort();
  }, [token]);

  if (status === "loading" && !project) {
    return <main className="view-project-state">Loading project…</main>;
  }
  if (status === "not-found") {
    return (
      <main className="view-project-state">
        <h1>Project not found</h1>
        <p>This shared link is unavailable or has been revoked.</p>
      </main>
    );
  }
  if (status === "error" || !project) {
    return (
      <main className="view-project-state">
        <h1>Unable to load this project</h1>
        <p>Please try again shortly.</p>
      </main>
    );
  }

  return (
    <main className="view-project">
      <header className="view-project__header">
        <Link className="view-project__brand" to="/">
          PJ Construction Design
        </Link>
      </header>
      <section className="view-project__intro">
        <p className="view-project__eyebrow">Your project</p>
        <h1>{project.name}</h1>
        {project.description ? <p>{project.description}</p> : null}
      </section>
      {project.photos.length ? (
        <section className="view-project__gallery" aria-label="Project photos">
          {project.photos.map((photo, index) => (
            <figure
              key={`${photo.imageUrl}-${index}`}
              className="view-project__photo"
            >
              <button
                type="button"
                className="view-project__photo-button"
                onClick={() => setLightboxIndex(index)}
                aria-label={`Open project photo ${index + 1}`}
              >
                <img
                  src={imageSource(photo.imageUrl)}
                  alt={photo.description || `Project photo ${index + 1}`}
                  loading="lazy"
                />
              </button>
              {photo.description ? (
                <figcaption>{photo.description}</figcaption>
              ) : null}
            </figure>
          ))}
        </section>
      ) : (
        <p className="view-project__empty">Photos will be added soon.</p>
      )}
      <Lightbox
        open={lightboxIndex !== null}
        close={() => setLightboxIndex(null)}
        index={lightboxIndex ?? 0}
        slides={project.photos.map((photo) => ({
          src: imageSource(photo.imageUrl),
          alt: photo.description || project.name,
        }))}
      />
    </main>
  );
}
